/**
 * Keep Pergola Classic paid add-ons tied to the parent line.
 * When the pergola is removed, matching height / heater / screen lines go with it.
 * Also clears any orphaned add-on lines left in the cart.
 */
(function () {
  const PARENT = "_sunnier_bundle_parent";
  const ADDON = "_sunnier_bundle_addon";
  const BUNDLE = "_sunnier_bundle";

  function rootUrl() {
    return (window.Shopify && Shopify.routes && Shopify.routes.root) || "/";
  }

  async function getCart() {
    const response = await fetch(rootUrl() + "cart.js", {
      headers: { Accept: "application/json" },
    });
    return response.json();
  }

  async function removeKeys(keys) {
    if (!keys.length) return null;
    const updates = {};
    keys.forEach((key) => {
      updates[key] = 0;
    });
    const response = await fetch(rootUrl() + "cart/update.js", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify({ updates }),
    });
    return response.json();
  }

  function parentBundles(cart) {
    const set = new Set();
    (cart.items || []).forEach((item) => {
      const props = item.properties || {};
      if (props[PARENT] && props[BUNDLE]) set.add(String(props[BUNDLE]));
    });
    return set;
  }

  function isPergolaParentHandle(handle) {
    const h = (handle || "").toLowerCase();
    return h === "pergola-classic" || h === "pergola-essential" || h.startsWith("pergola-");
  }

  function hasPergolaParent(cart) {
    return (cart.items || []).some((item) => {
      const handle = (item.handle || "").toLowerCase();
      const props = item.properties || {};
      return isPergolaParentHandle(handle) || props[PARENT];
    });
  }

  function orphanAddonKeys(cart) {
    const parents = parentBundles(cart);
    const pergolaPresent = hasPergolaParent(cart);
    return (cart.items || [])
      .filter((item) => {
        const props = item.properties || {};
        if (props[ADDON] && props[BUNDLE] && !parents.has(String(props[BUNDLE]))) {
          return true;
        }
        // Legacy free LED lines left after removing the pergola
        const isLegacyLed =
          Number(item.final_price || item.price || 0) === 0 &&
          (props["Addon type"] === "Perimeter LED Lighting" ||
            (item.handle || "").toLowerCase() === "pergola-classic-perimeter-led");
        if (isLegacyLed && !pergolaPresent) return true;
        return false;
      })
      .map((item) => item.key);
  }

  let cleaning = false;

  async function cleanupOrphans(cart) {
    const current = cart || (await getCart());
    const keys = orphanAddonKeys(current);
    if (!keys.length) return false;
    await removeKeys(keys);
    return true;
  }

  async function refreshCartUi() {
    const fresh = await getCart();
    if (window.FoxTheme && FoxTheme.pubsub && FoxTheme.pubsub.PUB_SUB_EVENTS) {
      FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, {
        sources: "sunnier-bundle-cleanup",
        cart: fresh,
      });
    }
    document.dispatchEvent(
      new CustomEvent("cart:refresh", { bubbles: true, detail: { cart: fresh } })
    );
    return fresh;
  }

  async function onCartUpdate(event) {
    if (cleaning) return;
    if (event && event.sources === "sunnier-bundle-cleanup") return;
    cleaning = true;
    try {
      const cart = (event && event.cart) || (await getCart());
      const removed = await cleanupOrphans(cart);
      if (removed) await refreshCartUi();
    } catch (error) {
      console.error("Sunnier cart bundle cleanup failed", error);
    } finally {
      cleaning = false;
    }
  }

  function patchCartItems() {
    const Ctor = customElements.get("cart-items");
    if (!Ctor || !Ctor.prototype || Ctor.prototype.__sunnierBundlePatched) return;

    const original = Ctor.prototype.updateQuantity;
    Ctor.prototype.updateQuantity = async function (line, quantity, name, target) {
      if (Number(quantity) === 0) {
        try {
          const cart = await getCart();
          const item = cart.items[Number(line) - 1];
          const props = (item && item.properties) || {};
          if (props[PARENT] && props[BUNDLE]) {
            const bundle = String(props[BUNDLE]);
            const siblingKeys = cart.items
              .filter((row) => {
                const p = row.properties || {};
                return p[ADDON] && String(p[BUNDLE]) === bundle;
              })
              .map((row) => row.key);
            if (siblingKeys.length) {
              cleaning = true;
              await removeKeys(siblingKeys);
              cleaning = false;
            }
          }
        } catch (error) {
          cleaning = false;
          console.error("Sunnier cart bundle remove failed", error);
        }
      }
      return original.call(this, line, quantity, name, target);
    };

    Ctor.prototype.__sunnierBundlePatched = true;
  }

  function removeCartNarrative() {
    document.querySelectorAll("[data-sunnier-cart-narrative]").forEach((el) => el.remove());
  }

  function boot() {
    removeCartNarrative();
    patchCartItems();
    cleanupOrphans()
      .then((removed) => {
        if (removed) return refreshCartUi();
      })
      .catch(() => {});

    if (window.FoxTheme && FoxTheme.pubsub && FoxTheme.pubsub.PUB_SUB_EVENTS) {
      FoxTheme.pubsub.subscribe(
        FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate,
        (event) => {
          onCartUpdate(event);
          removeCartNarrative();
        }
      );
    }
    document.addEventListener("cart:updated", (event) => {
      onCartUpdate((event && event.detail) || {});
      removeCartNarrative();
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot);
  } else {
    boot();
  }
  setTimeout(patchCartItems, 400);
  setTimeout(patchCartItems, 1200);
})();
